import { useEffect, useRef } from "react";
import { useAtom } from "jotai/react";

import { useSyncPreferredLanguageMutation } from "@/data/mutations/patientsMutations";
import { PatientType } from "@/domain/patients";
import { useAppLanguage } from "@/hooks/useAppLanguage";
import { pendingLanguageAtom } from "@/stores/atoms";
import { useCurrentUserAuthId } from "@/stores/authStore";

// Applying the server value here would restart the app immediately after login.
export const useSyncPreferredLanguage = (patient: PatientType | undefined) => {
  const [pendingLanguage, setPendingLanguage] = useAtom(pendingLanguageAtom);
  const currentUserAuthId = useCurrentUserAuthId();
  const { language } = useAppLanguage();
  const { mutate: syncPreferredLanguage } = useSyncPreferredLanguageMutation();

  // Failed syncs retry on the next app launch.
  const hasReconciledRef = useRef(false);

  useEffect(() => {
    if (!patient || hasReconciledRef.current) return;
    hasReconciledRef.current = true;

    if (!pendingLanguage) {
      // Coverage capture: the first time we see a patient with no stored
      // preference, persist the language they're currently using so English
      // and existing/old accounts get a preference without an explicit change.
      if (!patient.preferredLanguage) {
        syncPreferredLanguage({ language, shouldTrack: false });
      }
      return;
    }

    const isOwnChoice =
      pendingLanguage.authUserId === null ||
      pendingLanguage.authUserId === currentUserAuthId;

    if (!isOwnChoice) {
      // Prevent pending choices from leaking across accounts.
      setPendingLanguage(null);
      return;
    }

    syncPreferredLanguage({ language: pendingLanguage.language });
  }, [
    patient,
    pendingLanguage,
    currentUserAuthId,
    language,
    setPendingLanguage,
    syncPreferredLanguage,
  ]);
};
